import React, { Ref } from "react";
import { useTranslation } from "react-i18next";
import { BaseTextFieldProps } from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps,
} from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import TextField from "components/shared/TextField";

export type TDatePickerProps = Omit<
  MuiDatePickerProps<Dayjs>,
  "label" | "onChange" | "renderInput" | "value"
> &
  Pick<
    BaseTextFieldProps,
    "error" | "helperText" | "id" | "required" | "size" | "sx"
  > & {
    label?: string;
    value?: Dayjs | null;
    onChange?: (value: Dayjs | null) => void;
    onBlur?: () => void;
  };

const DatePicker = React.forwardRef(
  (
    {
      error,
      helperText,
      id,
      label,
      required,
      size,
      sx,
      value,
      onBlur,
      onChange,
      disabled,
      ...props
    }: TDatePickerProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const { i18n } = useTranslation();

    const handleChange = (value: Dayjs | null) => {
      onChange?.(value);
    };

    return (
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={i18n.language}
      >
        <MuiDatePicker
          data-testid={id}
          data-qaid={id}
          disabled={disabled}
          inputRef={ref}
          label={label}
          slotProps={{
            textField: () => ({
              ...(id && { id }),
              helperText,
              size,
              sx,
              required,
              onBlur,
              error: !disabled && error,
              FormHelperTextProps: {
                variant: "standard",
                "aria-live": "polite",
              },
            }),
          }}
          slots={{ textField: TextField }}
          value={value}
          onChange={handleChange}
          {...props}
        />
      </LocalizationProvider>
    );
  },
);

export default DatePicker;
